import { FC } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { CircleDollarSign, TriangleAlert } from 'lucide-react';

import {
    formatServiceFeeMoney,
    ServiceFeePageProps,
} from './serviceFeePresentation';
import { Routes } from '../../models/routes';
import { Alert, AlertDescription, AlertTitle } from '../ui/Alert';
import { Button } from '../ui/Button';

const formatDeadline = (value: string) =>
    new Intl.DateTimeFormat('uk-UA', {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));

const daysLabel = (days: number) => {
    if (days <= 0) return 'менше дня';
    if (days === 1) return '1 день';
    if (days >= 2 && days <= 4) return `${days} дні`;

    return `${days} днів`;
};

export const ServiceFeeBalanceNotice: FC = () => {
    const { serviceFeeBalanceStatus: status } =
        usePage<ServiceFeePageProps>().props;

    if (!status) return null;

    const paymentLink = `${Routes.Balance}?deposit=1`;
    const amountDue = formatServiceFeeMoney(status.pendingServiceFeesCents);

    if (status.confirmationBlocked) {
        return (
            <Alert
                variant="destructive"
                className="mt-6"
                data-testid="service-fee-balance-notice"
            >
                <TriangleAlert />
                <AlertTitle>Підтвердження замовлень призупинено</AlertTitle>
                <AlertDescription>
                    <p>
                        Сплатіть неоплачені сервісні збори на суму{' '}
                        <strong>{amountDue}</strong>, щоб відновити можливість
                        підтверджувати замовлення.
                    </p>
                    <Button asChild size="sm" variant="outline">
                        <Link href={paymentLink}>Поповнити баланс</Link>
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    if (status.gracePeriodActive && status.gracePeriodEndsAt) {
        const daysRemaining = status.gracePeriodDaysRemaining ?? 0;
        const urgent = daysRemaining <= 1;

        return (
            <Alert
                variant={urgent ? 'destructive' : 'warning'}
                className="mt-6"
                data-testid="service-fee-balance-notice"
            >
                <TriangleAlert />
                <AlertTitle>
                    {daysRemaining <= 3
                        ? `До обмеження залишилося ${daysLabel(daysRemaining)}`
                        : 'Почався пільговий період оплати'}
                </AlertTitle>
                <AlertDescription>
                    <p>
                        Поповніть баланс на <strong>{amountDue}</strong> до{' '}
                        <strong>
                            {formatDeadline(status.gracePeriodEndsAt)}
                        </strong>
                        {', щоб зберегти можливість підтверджувати замовлення.'}
                    </p>
                    <Button asChild size="sm" variant="outline">
                        <Link href={paymentLink}>Поповнити баланс</Link>
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    if (status.pendingServiceFeesCents > 0) {
        return (
            <Alert
                variant="warning"
                className="mt-6"
                data-testid="service-fee-balance-notice"
            >
                <TriangleAlert />
                <AlertTitle>Недостатньо коштів на балансі</AlertTitle>
                <AlertDescription>
                    <p>
                        Необхідно сплатити сервісні збори на суму{' '}
                        <strong>{amountDue}</strong>. Пільговий період
                        почнеться, коли доступний баланс буде вичерпано.
                    </p>
                    <Button asChild size="sm" variant="outline">
                        <Link href={paymentLink}>Поповнити баланс</Link>
                    </Button>
                </AlertDescription>
            </Alert>
        );
    }

    if (!status.lowBalance) return null;

    return (
        <Alert
            variant="warning"
            className="mt-6"
            data-testid="service-fee-balance-notice"
        >
            <CircleDollarSign />
            <AlertTitle>Баланс майже вичерпано</AlertTitle>
            <AlertDescription>
                <p>
                    На балансі залишилося не більше{' '}
                    {formatServiceFeeMoney(status.lowBalanceThresholdCents)}.
                    Поповніть його завчасно для автоматичної сплати сервісних
                    зборів.
                </p>
                <Button asChild size="sm" variant="outline">
                    <Link href={paymentLink}>Поповнити баланс</Link>
                </Button>
            </AlertDescription>
        </Alert>
    );
};
